// =============================================================================
// Painel do Gestor — a tela do critério de aceite
//
// "Cada gestor acessa o Painel com visão restrita aos centros de custo sob sua
// responsabilidade, acompanha orçamento anual e mensal, realizado, comprometido,
// saldo, forecast e desvios, e registra justificativa." (AF-01 §13)
//
// O recorte por centro de custo NÃO é feito aqui. Quem recorta é a RLS: o
// `financeiro` recebe todos os CCs, o `gestor_cc` só os do vínculo. Filtrar no
// front seria uma segunda regra de acesso, e a que o usuário consegue burlar.
// O seletor abaixo existe só para o Financeiro escolher qual CC olhar — para o
// gestor, ele vem com um item só.
// =============================================================================

import { Fragment, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buscarLancamentosDaCoordenada,
  buscarLancamentosDaLinha,
  buscarPainelGestor,
  type CentroCustoMes,
  type Lancamento,
  type LinhaMes,
} from "../lib/api.ts";
import { itensDeCategoria, type ItemCategoria } from "../lib/categoriasPainel.ts";
import { HeatmapCategoriaMes } from "../componentes/BlocosCategoria.tsx";
import {
  competenciaLonga,
  dataCurta,
  mesCurto,
  moeda,
  moedaCompacta,
  percentual,
  ROTULO_STATUS,
  TOM_STATUS,
  type Tom,
} from "../lib/formato.ts";
import {
  COR,
  CabecalhoGrafico,
  BarraFiltros,
  Campo,
  Kpi,
  LegendaGrafico,
  Painel,
  Pilula,
  TooltipRico,
  Vazio,
} from "../componentes/ui.tsx";
import { RodapeLinhagem } from "../componentes/RodapeLinhagem.tsx";
import { BlocoAlertas } from "../componentes/BlocoAlertas.tsx";
import { FiltroAno, FiltroMes } from "../componentes/FiltroPeriodo.tsx";
import { ModalJustificativa } from "./ModalJustificativa.tsx";
import { ModalLancamentos } from "./ModalLancamentos.tsx";
import { ModalRevisao } from "./ModalRevisao.tsx";

/** Paleta categórica do donut. Azuis da marca + o laranja como último acento. */
function somaCentavos(valores: string[]): string {
  return valores.reduce((acc, v) => acc + BigInt(v || "0"), 0n).toString();
}

/** O que o drilldown abre: a linha orçamentária de um mês, ou a coordenada. */
type AlvoDrilldown = Parameters<typeof ModalLancamentos>[0]["linha"];

/**
 * A coordenada do gasto sem orçamento, no formato que o modal entende.
 *
 * ⚠️ Objeto novo, sem a chave `linha_id`: o modal decide o caminho por
 * `"linha_id" in linha`, e o item da tela carrega `linha_id: null` — espalhá-lo
 * mandaria o modal buscar os lançamentos de uma linha que não existe.
 */
function coordenadaDoItem(l: ItemCategoria): AlvoDrilldown {
  return {
    centro_custo_id: l.centro_custo_id,
    centro_custo_codigo: l.centro_custo_codigo,
    centro_custo_nome: l.centro_custo_nome,
    categoria_id: l.foraDaLinha?.categoria_id ?? null,
    categoria_nome: l.categoria_nome,
    competencia: l.competencia,
    orcado_centavos: "0",
    realizado_centavos: l.realizado_centavos,
    pct_execucao: "0",
  };
}

/** Como a tela descreve o gasto que ficou fora da linha. Nulo para a linha. */
function rotuloFora(l: ItemCategoria, varios: boolean): string | null {
  if (!l.foraDaLinha) return null;
  return l.foraDaLinha.tem_orcamento
    ? "ainda sem linha atribuída"
    : `sem orçamento ${varios ? "nos meses do período" : "no mês"}`;
}

export function PainelGestor({ periodo, onMudarPeriodo }: {
  periodo: string[];
  onMudarPeriodo: (p: string[]) => void;
}) {
  const competencia = periodo[periodo.length - 1]!;
  const varios = periodo.length > 1;

  const qc = useQueryClient();
  const [ccSelecionado, setCcSelecionado] = useState<string | null>(null);
  const [pedindoRevisao, setPedindoRevisao] = useState(false);
  const [linhaJustificando, setLinhaJustificando] =
    useState<(LinhaMes & { linha_id: string }) | null>(null);
  const [linhaDetalhando, setLinhaDetalhando] = useState<AlvoDrilldown | null>(null);

  const painel = useQuery({
    queryKey: ["painel-gestor", periodo],
    queryFn: () => buscarPainelGestor(periodo),
  });

  const d = painel.data;

  // O percentual de YTD e o ritmo do ano — a régua do terceiro cartão novo.
  // ⚠️ O orçamento ANUAL da área é a soma das doze competências, não o YTD.
  //
  // O cartão "Orçamento anual da área" vinha do `orcado_ytd`, que é o acumulado
  // ATÉ o mês — em março ele mostrava um quarto do ano e chamava aquilo de
  // anual. Com os três cartões de YTD agora explícitos, a confusão ficaria
  // gritante; a série do exercício já vem na resposta e resolve.
  const orcadoAnualDoCc = useMemo(() => {
    if (!d) return null;
    const alvo = ccSelecionado ?? d.centros[0]?.centro_custo_id;
    if (!alvo) return null;
    return d.serie_ano
      .filter((x) => x.centro_custo_id === alvo)
      .reduce((t, x) => t + BigInt(x.orcado_centavos), 0n);
  }, [d, ccSelecionado]);

  // O CC vigente: o escolhido, ou o primeiro que a RLS devolveu. Para o gestor
  // com um CC só, o seletor some e isto resolve sozinho.
  const cc: CentroCustoMes | undefined = useMemo(() => {
    if (!d?.centros.length) return undefined;
    return d.centros.find((c) => c.centro_custo_id === ccSelecionado) ?? d.centros[0];
  }, [d, ccSelecionado]);

  // As linhas orçamentárias do CC E o gasto dele sem orçamento. Só as linhas
  // não fechariam com os cartões: categoria que gastou sem ter linha no mês
  // ficaria fora de todos os blocos por categoria (ver lib/categoriasPainel.ts).
  const linhasDoCc = useMemo(
    () => (d && cc ? itensDeCategoria(d.linhas, d.sem_linha ?? [], cc.centro_custo_id) : []),
    [d, cc],
  );

  const serieDoCc = useMemo(
    () => (d && cc ? d.serie_ano.filter((s) => s.centro_custo_id === cc.centro_custo_id) : []),
    [d, cc],
  );

  const ytdDoCc = useMemo(
    () =>
      d && cc
        ? d.ytd.find((y) => y.centro_custo_id === cc.centro_custo_id)
        : undefined,
    [d, cc],
  );

  // Forecast: o previsto das competências que ainda não chegaram, somado ao
  // realizado do que já passou. É o "até o fim do ano" do AF-01 §9.
  const forecastAnual = useMemo(() => {
    if (!d || !cc) return "0";
    const futuro = d.forecast
      .filter((f) => f.centro_custo_id === cc.centro_custo_id && f.competencia > competencia)
      .map((f) => f.previsto_centavos);
    const realizadoAteAgora = ytdDoCc?.realizado_ytd_centavos ?? "0";
    return somaCentavos([realizadoAteAgora, ...futuro]);
  }, [d, cc, competencia, ytdDoCc]);

  // A régua do cartão "YTD · % utilizado": o ritmo do ano é a fração dele que
  // já passou, e é o que distingue execução alta de execução apenas tardia.
  const ytdDoCcPct = ytdDoCc?.pct_execucao_ytd ?? null;
  const pctYtdNum = Number.parseFloat(ytdDoCcPct ?? "0");
  const pctYtdTom: Tom | undefined =
    !ytdDoCcPct ? undefined : pctYtdNum > 100 ? "risco" : pctYtdNum >= 90 ? "atencao" : "ok";
  const ritmoEsperadoAno = (Number(competencia.slice(5, 7)) / 12) * 100;

  if (painel.isPending) {
    return <Esqueleto />;
  }

  if (painel.isError) {
    const e = painel.error as { message?: string };
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Vazio
          titulo="Não foi possível carregar o painel"
          dica={e?.message ?? "Tente recarregar a página."}
        />
      </div>
    );
  }

  if (!d || !cc) {
    // ⚠️ Vazio no PERÍODO não é o mesmo que sem vínculo (achado em 06/10): o
    // painel parte do que o CC tem no mês — orçado ou gasto —, e um mês sem
    // nada (janeiro, antes de o orçamento do ano entrar) caía aqui dizendo
    // "não está vinculado", e sem o filtro de mês, deixando o gestor preso.
    // Agora o filtro fica, e a mensagem cobre os dois casos sem afirmar o que
    // não sabe.
    return (
      <div className="pagina pb-20">
        <BarraFiltros className="mt-7 mb-6">
          <Campo rotulo="Mês">
            <FiltroMes periodo={periodo} onMudar={onMudarPeriodo} />
          </Campo>
          <Campo rotulo="Ano">
            <FiltroAno periodo={periodo} onMudar={onMudarPeriodo} />
          </Campo>
        </BarraFiltros>
        <div className="mx-auto max-w-2xl px-6 py-10">
          <Vazio
            titulo="Nada a mostrar neste período"
            dica="Seu centro de custo não tem orçamento nem gasto no período escolhido — escolha outro mês. Se nenhum mês mostra nada, peça ao Financeiro o vínculo com o seu centro de custo."
          />
        </div>
      </div>
    );
  }

  const disponivelNegativo = BigInt(cc.disponivel_centavos) < 0n;

  return (
    <div className="pagina pb-20">
      {/* ── cabeçalho ────────────────────────────────────────────────────
          Título e subtítulo são os do protótipo. O centro de custo saiu do
          título e voltou para a barra de filtros, que é onde ele está lá — e
          onde faz sentido, porque é um controle, não um enfeite. */}
      <header className="pt-7 pb-5">
        <h1 className="font-display text-[22px] leading-tight font-bold tracking-tight">
          Meu orçamento
        </h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
          Acompanhamento do centro de custo sob sua responsabilidade.
        </p>
      </header>

      <BarraFiltros className="mb-6">
        <Campo rotulo="Centro de custo">
          <select
            aria-label="Centro de custo"
            value={cc.centro_custo_id}
            onChange={(e) => setCcSelecionado(e.target.value)}
            disabled={d.centros.length === 1}
            className="min-w-[240px] rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px] disabled:text-tinta-media"
          >
            {d.centros.map((c) => (
              <option key={c.centro_custo_id} value={c.centro_custo_id}>
                {c.codigo} · {c.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Mês">
          <FiltroMes periodo={periodo} onMudar={onMudarPeriodo} />
        </Campo>
        <Campo rotulo="Ano">
          <FiltroAno periodo={periodo} onMudar={onMudarPeriodo} />
        </Campo>

        {/* O protótipo tem este botão na barra de filtros, e ele não existia em
            lugar nenhum da aplicação — a rota `/api/revisoes` estava escrita e
            sem porta de entrada. É o caminho formal do gestor quando o teto não
            comporta o gasto: ele não edita o próprio orçamento (a RPC recusa),
            ele PEDE. */}
        <div className="ml-auto self-end">
          <button
            type="button"
            onClick={() => setPedindoRevisao(true)}
            className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul"
          >
            Solicitar revisão do orçamento
          </button>
        </div>
      </BarraFiltros>

      {/* ── KPIs ─────────────────────────────────────────────────────────
          Os seis do protótipo do Painel do Gestor, na ordem dele, mais os três
          de YTD pedidos pelo Financeiro em 21/09. Nove cartões fecham a grade
          de três colunas do `.kpi-grid`.

          ⚠️ Aqui a borda do cartão é em CIMA, de 3px: é a anatomia que o
          protótipo usa nesta tela, diferente da Despesa, que usa borda à
          esquerda de 4px.

          O alternador Mês / Acumulado no ano saiu: o acumulado virou cartão
          fixo, e o recorte de tempo agora é o filtro de período. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-3.5">
        <Kpi
          borda="topo"
          rotulo="Orçamento anual da área"
          valor={moeda(orcadoAnualDoCc === null ? null : String(orcadoAnualDoCc))}
          apoio={`exercício ${d.exercicio}`}
          dica="Soma das doze competências do exercício para este centro de custo — o ano inteiro, não o acumulado até aqui."
        />
        <Kpi
          borda="topo"
          rotulo={varios ? "Orçamento do período" : "Orçamento do mês"}
          valor={moeda(cc.orcado_centavos)}
        />
        <Kpi
          borda="topo"
          rotulo={varios ? "Realizado no período" : "Realizado no mês"}
          valor={moeda(cc.realizado_centavos)}
        />
        <Kpi
          borda="topo"
          rotulo="Comprometido"
          valor={moeda(cc.comprometido_centavos)}
          dica="Lançamento registrado no Conta Azul e ainda não pago, vencido ou não. Compromisso que ainda não foi lançado lá não entra neste número. Diferente de provisionado, que é orçado sem nenhuma ocorrência esperada."
        />
        <Kpi
          borda="topo"
          rotulo={varios ? "Saldo disponível (período)" : "Saldo disponível (mês)"}
          valor={moeda(cc.disponivel_centavos)}
          tom={disponivelNegativo ? "risco" : undefined}
          destaque
        />
        <Kpi
          borda="topo"
          rotulo="Forecast (fim do ano)"
          valor={moeda(forecastAnual)}
          apoio="realizado + previsto dos meses restantes"
          dica="Soma do que já foi realizado com o previsto das competências futuras. Linha recorrente projeta pelo próprio valor; variável, pela média dos três últimos meses fechados."
        />

        {/* Os três de YTD, pedidos em 21/09. Ficam juntos e no fim, para se
            lerem como um bloco: quanto foi orçado no ano até aqui, quanto se
            gastou, e quanto disso o gasto consumiu. */}
        <Kpi
          borda="topo"
          rotulo="YTD · orçamento"
          valor={moeda(ytdDoCc?.orcado_ytd_centavos ?? null)}
          apoio={`de janeiro a ${competenciaLonga(competencia)}`}
          dica="Acumulado no ano: soma do orçado das competências do exercício até a selecionada."
        />
        <Kpi
          borda="topo"
          rotulo="YTD · realizado"
          valor={moeda(ytdDoCc?.realizado_ytd_centavos ?? null)}
          apoio={`de janeiro a ${competenciaLonga(competencia)}`}
          dica="Acumulado no ano do que fechou a competência, agregado por centro de custo."
        />
        <Kpi
          borda="topo"
          rotulo="YTD · % utilizado do orçado"
          valor={percentual(ytdDoCc?.pct_execucao_ytd ?? null)}
          tom={pctYtdTom}
          apoio={`ritmo esperado: ${percentual(ritmoEsperadoAno)}`}
          dica="Realizado acumulado sobre orçado acumulado. O ritmo esperado é a fração do ano que já passou — é ele que diz se o percentual é alto ou apenas tardio."
        />
      </div>

      {/* ── alertas do AF-01 (053) ─────────────────────────────────────────
          Teto atingido, mensalidade que variou e consumo perto do teto, do CC
          vigente. Justificar é ato sobre a linha de UM mês: com vários meses
          no filtro, o botão não aparece (mesma regra dos desvios). */}
      <BlocoAlertas
        periodo={periodo}
        centroCustoId={cc.centro_custo_id}
        onJustificar={varios ? undefined : (a) => {
          const l = d.linhas.find((x) => x.linha_id === a.linha_id);
          if (l) setLinhaJustificando({ ...l, linha_id: a.linha_id });
        }}
        onPedirRevisao={() => setPedindoRevisao(true)}
      />

      {/* ── consumo do orçamento ANUAL ───────────────────────────────────
          ⚠️ O protótipo mede o consumo do ANO, não o do mês, e põe a marca do
          ritmo esperado na barra. A diferença é o que a tela responde: o mês
          diz "estourei setembro"; o ano, com o ritmo ao lado, diz "estou
          gastando mais rápido do que o ano permite" — que é a pergunta que
          leva o gestor a agir antes do estouro. */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Consumo do orçamento anual"
          dica="Realizado acumulado sobre o orçado acumulado do exercício. A marca na barra é o ritmo esperado: a fração do ano que já passou."
        />
        <p className="mb-2.5 text-[12.5px] text-tinta-media">
          <span className="num font-semibold">{percentual(ytdDoCcPct)}</span> do orçamento anual
          consumido até {competenciaLonga(competencia)} · ritmo esperado para o período:{" "}
          <span className="num font-semibold">{percentual(ritmoEsperadoAno)}</span>
        </p>
        <BarraComRitmo pct={pctYtdNum} ritmo={ritmoEsperadoAno} tom={pctYtdTom ?? "ok"} />
      </Painel>

      {/* ── orçado × realizado por mês, acumulado ─────────────────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Orçado × realizado por mês"
          dica="Valores ACUMULADOS mês a mês, como no protótipo: cada ponto é o total do ano até ali. O acumulado mostra se a distância entre orçado e realizado está abrindo ou fechando, o que o valor mensal isolado esconde."
        />
        <GraficoMensal serie={serieDoCc} ate={competencia} acumulado />
      </Painel>

      {/* ── gastos do mês por categoria ───────────────────────────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Gastos do mês por categoria"
          dica="Barras horizontais com o realizado de cada categoria; a barra fica vermelha quando passa do orçado da própria linha, e amarela quando a categoria gastou sem ter orçamento. A soma das barras é o realizado do cartão."
        />
        <BarrasPorCategoria linhas={linhasDoCc} varios={varios} />
      </Painel>

      {/* ── orçado × realizado por categoria, com drilldown ───────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Orçado × realizado por categoria"
          dica="Clique numa categoria para ver os lançamentos que a compõem. Exigência do AF-01 §10: drilldown até o lançamento de origem, em ambas as visões. Categoria que gastou sem orçamento aparece como Não orçado."
        />
        <TabelaCategorias
          linhas={linhasDoCc}
          varios={varios}
          onJustificar={(l) => setLinhaJustificando(l)}
        />
      </Painel>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        {/* ── saldo por categoria ─────────────────────────────────────────── */}
        <Painel>
          <CabecalhoGrafico
            titulo="Saldo por categoria"
            dica="Orçado menos realizado e comprometido, na competência. Barra à esquerda do zero é categoria que já passou do teto."
          />
          <SaldoPorCategoria linhas={linhasDoCc} />
        </Painel>

        {/* ── ranking ─────────────────────────────────────────────────────── */}
        <Painel>
          <CabecalhoGrafico
            titulo="Ranking das maiores despesas"
            dica="Os cinco maiores lançamentos da competência, com o valor ao lado. É por onde o gestor reconhece o gasto que puxou o mês."
          />
          <RankingDespesas itens={(d.ranking ?? []).slice(0, 5)} />
        </Painel>
      </div>

      {/* ── projeção até dezembro ─────────────────────────────────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Projeção até dezembro"
          dica="Linha sólida: realizado. Linha tracejada: projeção pelo ritmo de gasto (média dos três últimos meses fechados). Metodologia simplificada — não substitui o forecast formal do Financeiro."
        />
        <GraficoMensal serie={serieDoCc} ate={competencia} projecao />
      </Painel>

      {/* ── heatmap categoria × mês ───────────────────────────────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Heatmap de consumo por categoria"
          dica="Percentual do orçado consumido por categoria em CADA mês do exercício. É onde se vê o padrão: categoria que estoura sempre é outro problema do que categoria que estourou uma vez."
        />
        <HeatmapCategoriaMes
          exercicio={d.exercicio}
          centroCustoId={cc.centro_custo_id}
          ate={competencia}
        />
      </Painel>

      {/* ── principais desvios ────────────────────────────────────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Principais desvios do mês"
          dica="Linhas que ultrapassaram o próprio orçado e categorias que gastaram sem orçamento, ordenadas pelo valor do desvio."
        />
        <TabelaDesvios
          linhas={linhasDoCc}
          varios={varios}
          onVer={(alvo) => setLinhaDetalhando(alvo)}
        />
      </Painel>

      {/* ── blocos do protótipo que dependem de outras frentes ────────────
          Aparecem porque o protótipo os tem e a tela do gestor precisa ser
          reconhecível na demo. ⚠️ Vazios e dizendo por quê: um bloco com
          exemplo inventado ao lado de números reais é o jeito mais rápido de
          alguém tomar decisão sobre um pedido que não existe. */}
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Painel>
          <CabecalhoGrafico
            titulo="Pedidos aguardando aprovação"
            dica="Solicitação → aprovação → lançamento. É a Frente 10 (Compras e Aprovação), de Segunda Fase."
          />
          <Vazio
            titulo="Previsto para a Frente 10"
            dica="O fluxo de compras e aprovação ainda não existe no sistema; hoje as solicitações entram pelo Arena. Quando a frente entrar, os pedidos deste centro de custo aparecem aqui."
          />
        </Painel>
        <Painel>
          <CabecalhoGrafico
            titulo="Comentários solicitados pelo financeiro"
            dica="Pedido de explicação do Financeiro sobre um gasto específico, respondido pelo gestor."
          />
          <Vazio
            titulo="Sem origem de dado na P1"
            dica="O que existe hoje é a justificativa de desvio, que o gestor registra na tabela acima e o Financeiro lê na Central Operacional. O pedido de comentário em si é frente de Segunda Fase."
          />
        </Painel>
      </div>

      {/* ── minhas pendências ─────────────────────────────────────────────
          O bloco que fecha a tela do gestor no protótipo, e o único dos três
          finais que tem dado hoje: linha excedente é pendência DELE até
          justificar. */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Minhas pendências"
          dica="Linhas excedentes deste centro de custo. Item sem justificativa registrada precisa de uma resposta sua antes de entrar na fila do Financeiro."
        />
        <MinhasPendencias linhas={linhasDoCc} onJustificar={(l) => setLinhaJustificando(l)} />
      </Painel>

      {pedindoRevisao && (
        <ModalRevisao
          ano={Number(competencia.slice(0, 4))}
          centroCustoId={cc.centro_custo_id}
          onFechar={() => setPedindoRevisao(false)}
        />
      )}

      <RodapeLinhagem />

      {linhaJustificando && (
        <ModalJustificativa
          linha={linhaJustificando}
          onFechar={() => setLinhaJustificando(null)}
          onSalvo={() => {
            setLinhaJustificando(null);
            void painel.refetch();
            void qc.invalidateQueries({ queryKey: ["alertas"] });
          }}
        />
      )}
      {linhaDetalhando && (
        <ModalLancamentos linha={linhaDetalhando} onFechar={() => setLinhaDetalhando(null)} />
      )}
    </div>
  );
}

// ── blocos internos ─────────────────────────────────────────────────────────

function Esqueleto() {
  return (
    <div className="pagina animate-pulse py-8">
      <div className="h-8 w-72 rounded-lg bg-superficie-2" />
      <div className="mt-6 grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-3.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-[88px] rounded-lg border border-linha bg-superficie" />
        ))}
      </div>
      <div className="mt-4 h-64 rounded-lg border border-linha bg-superficie" />
    </div>
  );
}

/**
 * Barra de consumo com a marca do ritmo esperado.
 *
 * A marca é o que o protótipo acrescenta e faz a barra responder à pergunta
 * certa: 60% consumido é ótimo em dezembro e alarmante em março. Sem a régua,
 * cada um compara com o número que tem na cabeça.
 */
function BarraComRitmo({ pct, ritmo, tom }: { pct: number; ritmo: number; tom: Tom }) {
  const cor =
    tom === "risco" ? "bg-risco-tinta" : tom === "atencao" ? "bg-atencao-tinta" : "bg-ok-tinta";
  return (
    <div>
      <div className="relative h-3 overflow-hidden rounded-full bg-superficie-2">
        <div className={`h-full rounded-full ${cor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        {/* A marca fica FORA do `overflow-hidden` do preenchimento, senão some
            quando o consumo passa dela. */}
      </div>
      <div className="relative h-4">
        <span
          aria-hidden
          className="absolute top-0 -mt-[18px] h-3 w-0.5 bg-azul"
          style={{ left: `${Math.min(ritmo, 100)}%` }}
        />
        <span
          className="num absolute top-0 -translate-x-1/2 text-[10.5px] text-tinta-fraca"
          style={{ left: `${Math.min(ritmo, 100)}%` }}
        >
          ritmo {percentual(ritmo, 0)}
        </span>
      </div>
      <div className="num flex justify-between text-[10.5px] text-tinta-fraca">
        <span>0%</span>
        <span>100%</span>
      </div>
    </div>
  );
}

/**
 * Gastos do mês por categoria — barras horizontais.
 *
 * Trocou a rosca. Com nome de categoria longo, a rosca manda o olho ao rodapé
 * da legenda e de volta a cada fatia; em barra, o nome fica encostado no valor.
 * A barra vermelha é a que passou do orçado da própria linha, que é o destaque
 * que o protótipo pede.
 */
function BarrasPorCategoria({ linhas, varios }: { linhas: ItemCategoria[]; varios: boolean }) {
  const comGasto = linhas
    .filter((l) => BigInt(l.realizado_centavos) > 0n)
    .sort((a, b) => (BigInt(b.realizado_centavos) > BigInt(a.realizado_centavos) ? 1 : -1));

  if (comGasto.length === 0) return <Vazio titulo="Nenhum gasto realizado neste mês" />;

  const maior = BigInt(comGasto[0]!.realizado_centavos);
  const total = comGasto.reduce((t, l) => t + BigInt(l.realizado_centavos), 0n);

  return (
    <ul className="thin-scroll max-h-[320px] overflow-y-auto pr-1">
      {comGasto.map((l) => {
        const real = BigInt(l.realizado_centavos);
        const orcado = BigInt(l.orcado_centavos);
        const estourou = orcado > 0n && real > orcado;
        const fora = rotuloFora(l, varios);
        const largura = maior === 0n ? 0 : Number((real * 1000n) / maior) / 10;
        return (
          <li key={l.chave} className="border-t border-linha py-2 first:border-t-0">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-[13px] font-medium" title={l.categoria_nome}>
                {l.categoria_nome}
              </span>
              <span className="num shrink-0 text-[12.5px] font-bold">
                {moeda(l.realizado_centavos)}
              </span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-superficie-2">
              <div
                className={`h-full rounded-full ${
                  estourou ? "bg-risco-tinta" : fora ? "bg-atencao-tinta" : "bg-azul-medio"
                }`}
                style={{ width: `${largura}%` }}
              />
            </div>
            <p className="num mt-1 text-[11px] text-tinta-fraca">
              {percentual(total === 0n ? 0 : Number((real * 10000n) / total) / 100)}{" "}
              {varios ? "do período" : "do mês"} ·{" "}
              {fora ? (
                <span className="font-semibold text-atencao-tinta">{fora}</span>
              ) : (
                <>de {moeda(l.orcado_centavos)} orçados</>
              )}
              {estourou && <span className="font-semibold text-risco-tinta"> · acima do teto</span>}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Orçado × realizado por categoria, com o drilldown aberto na própria linha.
 *
 * O protótipo expande a categoria ali mesmo (`▸`), sem tirar a pessoa da tela —
 * e o AF-01 §10 exige o caminho até o lançamento de origem. Abrir num modal
 * também cumpriria, mas perde o contexto das outras categorias, que é o que faz
 * alguém comparar antes de justificar.
 */
function TabelaCategorias({
  linhas,
  varios,
  onJustificar,
}: {
  linhas: ItemCategoria[];
  varios: boolean;
  onJustificar: (l: LinhaMes & { linha_id: string }) => void;
}) {
  const [aberta, setAberta] = useState<string | null>(null);

  if (linhas.length === 0) {
    return (
      <Vazio
        titulo="Nenhuma linha orçamentária nem gasto nesta competência"
        dica="O orçamento deste centro de custo ainda não foi cadastrado para o período, e nenhum gasto foi registrado nele."
      />
    );
  }

  return (
    <div className="thin-scroll overflow-x-auto">
      <table className="w-full min-w-[760px] text-[13px]">
        <thead>
          <tr className="text-[11px] tracking-wide text-tinta-fraca uppercase">
            <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Categoria</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Orçado</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Realizado</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Saldo</th>
            <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Status</th>
            <th className="border-b-2 border-azul px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            // Gasto fora da linha abre pela coordenada, que só existe com UM
            // mês: com vários, a competência do item é só a última do período.
            const abre = !!l.linha_id || (!!l.foraDaLinha && !varios);
            const aberto = aberta === l.chave;
            const tom = TOM_STATUS[l.status] ?? "neutro";
            const precisaJustificar = l.status === "excedente" && !!l.linha_id;
            return (
              <Fragment key={l.chave}>
                <tr
                  className={`border-b border-linha ${abre ? "cursor-pointer hover:bg-superficie-2" : ""}`}
                  onClick={() => abre && setAberta(aberto ? null : l.chave)}
                >
                  <td className="px-2 py-2">
                    <span aria-hidden className="mr-1.5 inline-block w-3 text-tinta-fraca">
                      {abre ? (aberto ? "▾" : "▸") : ""}
                    </span>
                    {l.categoria_nome}
                    {l.fornecedor_nome && (
                      <span className="block pl-[18px] text-[11px] text-tinta-fraca">
                        {l.fornecedor_nome}
                      </span>
                    )}
                  </td>
                  <td className="num px-2 py-2 text-right">
                    {l.foraDaLinha ? (
                      <span className="text-tinta-fraca" title={rotuloFora(l, varios) ?? undefined}>—</span>
                    ) : (
                      moeda(l.orcado_centavos)
                    )}
                  </td>
                  <td className="num px-2 py-2 text-right">{moeda(l.realizado_centavos)}</td>
                  <td
                    className={`num px-2 py-2 text-right ${
                      BigInt(l.disponivel_centavos) < 0n ? "font-semibold text-risco-tinta" : ""
                    }`}
                  >
                    {moeda(l.disponivel_centavos)}
                  </td>
                  <td className="px-2 py-2">
                    <Pilula tom={tom}>{ROTULO_STATUS[l.status] ?? l.status}</Pilula>
                  </td>
                  <td className="px-2 py-2 text-right">
                    {precisaJustificar && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onJustificar({ ...l, linha_id: l.linha_id! });
                        }}
                        className="rounded-lg border border-linha px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap hover:border-laranja hover:text-laranja-tinta"
                      >
                        Justificar
                      </button>
                    )}
                  </td>
                </tr>
                {aberto && (
                  <tr>
                    <td colSpan={6} className="border-b border-linha bg-superficie-2/40 px-2 py-2">
                      {l.linha_id ? (
                        <LancamentosDaLinha linhaId={l.linha_id} />
                      ) : (
                        <LancamentosDaCoordenada
                          centroCustoId={l.centro_custo_id}
                          categoriaId={l.foraDaLinha?.categoria_id ?? null}
                          competencia={l.competencia}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Os lançamentos de uma linha, abertos dentro da própria tabela. */
function LancamentosDaLinha({ linhaId }: { linhaId: string }) {
  const q = useQuery({
    queryKey: ["lancamentos", linhaId],
    queryFn: () => buscarLancamentosDaLinha(linhaId),
  });

  if (q.isPending) return <p className="py-2 text-[12px] text-tinta-fraca">Carregando…</p>;
  if (!q.data?.lancamentos.length) {
    return (
      <p className="py-2 text-[12px] text-tinta-fraca">
        Nenhum lançamento casado com esta linha. O realizado dela é zero, ou os lançamentos ainda
        estão na fila de exceções esperando alocação.
      </p>
    );
  }

  return <ListaLancamentosInline lancamentos={q.data.lancamentos} />;
}

/**
 * Os lançamentos do gasto fora da linha: pela coordenada (centro de custo +
 * categoria + mês), porque não há linha orçamentária para abrir.
 *
 * ⚠️ Só os que não têm linha. A rota devolve a coordenada inteira, e numa
 * categoria COM orçamento isso inclui o que já caiu na linha de cima — a lista
 * somaria mais que o item, e o gestor contaria o mesmo lançamento duas vezes.
 */
function LancamentosDaCoordenada({
  centroCustoId,
  categoriaId,
  competencia,
}: {
  centroCustoId: string;
  categoriaId: string | null;
  competencia: string;
}) {
  const q = useQuery({
    queryKey: ["lancamentos-coordenada", centroCustoId, categoriaId, competencia],
    queryFn: () => buscarLancamentosDaCoordenada(centroCustoId, categoriaId, competencia),
  });

  if (q.isPending) return <p className="py-2 text-[12px] text-tinta-fraca">Carregando…</p>;
  const semLinha = (q.data?.lancamentos ?? []).filter((lc) => lc.linha_id === null);
  if (!semLinha.length) {
    return (
      <p className="py-2 text-[12px] text-tinta-fraca">
        Nenhum lançamento fora de linha nesta categoria no mês. Se acabou de ser atribuído, recarregue
        o painel.
      </p>
    );
  }

  return <ListaLancamentosInline lancamentos={semLinha} />;
}

function ListaLancamentosInline({
  lancamentos,
}: {
  lancamentos: Lancamento[];
}) {
  return (
    <table className="w-full text-[12px]">
      <tbody>
        {lancamentos.map((lc, i) => (
          <tr key={lc.lancamento_id ?? `total-${i}`} className="border-b border-linha/60 last:border-b-0">
            <td className="py-1.5 pl-6">{lc.descricao ?? "(sem descrição)"}</td>
            <td className="py-1.5 text-tinta-fraca">{lc.fornecedor_nome ?? "—"}</td>
            <td className="num py-1.5 text-right text-tinta-fraca">{dataCurta(lc.data_vencimento)}</td>
            <td className="num py-1.5 text-right font-semibold">
              {moeda(lc.valor_rateado_centavos)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Saldo por categoria: barra para a direita sobra, para a esquerda estouro. */
function SaldoPorCategoria({ linhas }: { linhas: ItemCategoria[] }) {
  if (linhas.length === 0) return <Vazio titulo="Sem categorias nesta competência" />;

  // O gasto fora da linha entra com saldo negativo — é o que ele é: consumiu
  // um teto de zero. O sufixo evita que duas barras com o mesmo nome se
  // confundam quando a categoria tem linha e também gasto fora dela.
  const dados = linhas
    .map((l) => ({
      nome: !l.foraDaLinha
        ? l.categoria_nome
        : `${l.categoria_nome} (${l.foraDaLinha.tem_orcamento ? "sem linha" : "sem orçamento"})`,
      saldo: Number(BigInt(l.disponivel_centavos)) / 100,
    }))
    .sort((a, b) => a.saldo - b.saldo);

  return (
    <div style={{ height: Math.max(dados.length * 34, 120) }}>
      <ResponsiveContainer>
        <BarChart data={dados} layout="vertical" margin={{ left: 4, right: 12, top: 4, bottom: 4 }}>
          <XAxis
            type="number"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10.5, fill: "#6b7789" }}
            tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))}
          />
          <YAxis
            type="category"
            dataKey="nome"
            width={130}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#48566e" }}
          />
          <ReferenceLine x={0} stroke="#c3cbd9" />
          <Tooltip
            cursor={{ fill: "rgba(16,40,80,0.04)" }}
            content={({ active, payload }) => {
              const p = payload?.[0]?.payload as { nome: string; saldo: number } | undefined;
              return active && p ? (
                <TooltipRico
                  titulo={p.nome}
                  itens={[
                    {
                      rotulo: p.saldo >= 0 ? "Saldo disponível" : "Acima do teto",
                      valor: Math.abs(p.saldo).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }),
                    },
                  ]}
                />
              ) : null;
            }}
          />
          <Bar dataKey="saldo" radius={2} maxBarSize={18}>
            {dados.map((x, i) => (
              <Cell key={i} fill={x.saldo >= 0 ? COR.positivo : COR.negativo} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Top 5 do mês, com o valor ao lado — o formato do protótipo. */
function RankingDespesas({
  itens,
}: {
  itens: {
    /** Nulo no total de uma categoria de pessoal (051). */
    lancamento_id: string | null;
    descricao: string | null;
    fornecedor_nome: string | null;
    valor_centavos: string;
  }[];
}) {
  if (itens.length === 0) return <Vazio titulo="Nenhum lançamento nesta competência" />;

  const maior = BigInt(itens[0]!.valor_centavos);
  return (
    <ol className="thin-scroll max-h-[300px] overflow-y-auto pr-1">
      {itens.map((i, pos) => (
        <li key={i.lancamento_id ?? `total-${pos}`} className="border-t border-linha py-2 first:border-t-0">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-[13px]" title={i.descricao ?? ""}>
              <span className="num mr-1.5 text-tinta-fraca">{pos + 1}.</span>
              {i.descricao ?? "(sem descrição)"}
            </span>
            <span className="num shrink-0 text-[13px] font-bold">{moeda(i.valor_centavos)}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-superficie-2">
            <div
              className="h-full rounded-full bg-azul-medio"
              style={{
                width: `${maior === 0n ? 0 : Number((BigInt(i.valor_centavos) * 1000n) / maior) / 10}%`,
              }}
            />
          </div>
          {i.fornecedor_nome && (
            <p className="mt-0.5 text-[11px] text-tinta-fraca">{i.fornecedor_nome}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Principais desvios: tabela, com o caminho para o lançamento. */
function TabelaDesvios({
  linhas,
  varios,
  onVer,
}: {
  linhas: ItemCategoria[];
  varios: boolean;
  onVer: (alvo: AlvoDrilldown) => void;
}) {
  // O lançamento que ainda espera alocação numa categoria COM orçamento não é
  // desvio: o teto dele está na linha, e é lá que o desvio aparece depois da
  // alocação. O gasto sem orçamento nenhum é desvio, e entra.
  const acima = linhas
    .filter((l) => !l.foraDaLinha?.tem_orcamento)
    .filter((l) => BigInt(l.realizado_centavos) > BigInt(l.orcado_centavos))
    .sort((a, b) => {
      const da = BigInt(a.realizado_centavos) - BigInt(a.orcado_centavos);
      const db = BigInt(b.realizado_centavos) - BigInt(b.orcado_centavos);
      return db > da ? 1 : db < da ? -1 : 0;
    });

  if (acima.length === 0) {
    return (
      <Vazio
        titulo="Nenhuma linha acima do orçado nesta competência"
        dica="Aparecem aqui as linhas cujo realizado passou do próprio teto, da maior diferença para a menor."
      />
    );
  }

  return (
    <div className="thin-scroll overflow-x-auto">
      <table className="w-full min-w-[620px] text-[13px]">
        <thead>
          <tr className="text-[11px] tracking-wide text-tinta-fraca uppercase">
            <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Linha</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">
              Realizado / Orçado
            </th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Desvio</th>
            <th className="border-b-2 border-azul px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {acima.map((l) => {
            const desvio = BigInt(l.realizado_centavos) - BigInt(l.orcado_centavos);
            const pct =
              BigInt(l.orcado_centavos) === 0n
                ? null
                : Number((desvio * 10000n) / BigInt(l.orcado_centavos)) / 100;
            // Linha abre pelo `linha_id`; gasto sem orçamento, pela coordenada —
            // que só existe com um mês selecionado.
            const alvo: AlvoDrilldown | null = l.linha_id
              ? { ...l, linha_id: l.linha_id }
              : l.foraDaLinha && !varios
                ? coordenadaDoItem(l)
                : null;
            return (
              <tr key={l.chave} className="border-b border-linha">
                <td className="px-2 py-2">
                  {l.categoria_nome}
                  {l.fornecedor_nome && (
                    <span className="block text-[11px] text-tinta-fraca">{l.fornecedor_nome}</span>
                  )}
                </td>
                <td className="num px-2 py-2 text-right">
                  {moeda(l.realizado_centavos)}
                  <span className="block text-[11px] text-tinta-fraca">
                    {l.foraDaLinha ? "sem orçamento" : <>de {moeda(l.orcado_centavos)}</>}
                  </span>
                </td>
                <td className="num px-2 py-2 text-right font-semibold text-risco-tinta">
                  {moeda(String(desvio))}
                  {pct !== null && (
                    <span className="block text-[11px] font-normal text-tinta-fraca">
                      {percentual(pct)} acima
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  {alvo && (
                    <button
                      type="button"
                      onClick={() => onVer(alvo)}
                      className="text-[12px] font-semibold text-azul-medio hover:underline"
                    >
                      Ver lançamento
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Minhas pendências: o que espera uma resposta do gestor. */
function MinhasPendencias({
  linhas,
  onJustificar,
}: {
  linhas: LinhaMes[];
  onJustificar: (l: LinhaMes & { linha_id: string }) => void;
}) {
  const pendentes = linhas.filter(
    (l) => l.status === "excedente" || l.status === "excedente_aguardando_ajuste",
  );

  if (pendentes.length === 0) {
    return (
      <Vazio
        titulo="Nada esperando por você"
        dica="Linha que passa do teto aparece aqui até ser justificada. Enquanto isso, ela conta como desvio na visão do Financeiro."
      />
    );
  }

  return (
    <>
      <ul>
        {pendentes.map((l) => (
          <li
            key={`${l.categoria_id}-${l.competencia}`}
            className="flex flex-wrap items-center justify-between gap-3 border-b border-linha py-2.5 last:border-b-0"
          >
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium">{l.categoria_nome}</p>
              <p className="num text-[11.5px] text-tinta-fraca">
                {moeda(l.realizado_centavos)} de {moeda(l.orcado_centavos)} ·{" "}
                <Pilula tom={TOM_STATUS[l.status] ?? "risco"}>
                  {ROTULO_STATUS[l.status] ?? l.status}
                </Pilula>
              </p>
            </div>
            {l.linha_id && (
              <button
                type="button"
                onClick={() => onJustificar({ ...l, linha_id: l.linha_id! })}
                className="shrink-0 rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold hover:border-laranja hover:text-laranja-tinta"
              >
                Justificar
              </button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-tinta-fraca">
        Itens sem justificativa registrada precisam de uma resposta sua antes de entrarem na fila do
        Financeiro.
      </p>
    </>
  );
}


/**
 * O gráfico mensal, nos dois formatos que o protótipo pede.
 *
 * `acumulado` — "Orçado x realizado por mês", com valores ACUMULADOS: cada
 * ponto é o total do ano até ali. É o que mostra se a distância entre orçado e
 * realizado está abrindo ou fechando, coisa que a barra mensal isolada esconde.
 *
 * `projecao` — "Projeção até dezembro": realizado em linha sólida até o mês
 * corrente, e daí em diante tracejada, pelo ritmo dos três últimos meses
 * fechados. ⚠️ Metodologia simplificada, e a dica da tela diz isso: não
 * substitui o forecast formal do Financeiro.
 */
function GraficoMensal({
  serie,
  ate,
  acumulado = false,
  projecao = false,
}: {
  serie: CentroCustoMes[];
  ate: string;
  acumulado?: boolean;
  projecao?: boolean;
}) {
  const dados = useMemo(() => {
    const ordenada = [...serie].sort((a, b) => a.competencia.localeCompare(b.competencia));
    if (ordenada.length === 0) return [];

    // Ritmo: média dos três últimos meses FECHADOS antes do corrente. Mesmo
    // critério da `v_forecast_linha` depois da migration 029.
    const fechados = ordenada.filter((x) => x.competencia < ate).slice(-3);
    const ritmo =
      fechados.length === 0
        ? 0
        : fechados.reduce((t, x) => t + Number(BigInt(x.realizado_centavos)), 0) / fechados.length;

    let accOrcado = 0;
    let accReal = 0;
    let accProj = 0;

    return ordenada.map((x) => {
      const passado = x.competencia <= ate;
      const orcado = Number(BigInt(x.orcado_centavos)) / 100;
      const real = Number(BigInt(x.realizado_centavos)) / 100;
      accOrcado += orcado;
      if (passado) accReal += real;
      accProj += passado ? real : ritmo / 100;

      return {
        mes: mesCurto(x.competencia),
        orcado: acumulado ? accOrcado : orcado,
        realizado: passado ? (acumulado ? accReal : real) : null,
        // A projeção repete o último ponto realizado para as duas linhas se
        // encontrarem; sem isso a tracejada nasce solta no meio do gráfico.
        projetado: projecao ? (x.competencia >= ate ? accProj : null) : null,
      };
    });
  }, [serie, ate, acumulado, projecao]);

  if (dados.length === 0) return <Vazio titulo="Sem orçamento cadastrado para este exercício" />;

  return (
    <>
      <div className="h-[280px] w-full">
        <ResponsiveContainer>
          <ComposedChart data={dados} margin={{ top: 8, right: 8, left: 8 }}>
            <CartesianGrid stroke="#eef1f6" vertical={false} />
            <XAxis
              dataKey="mes"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "#6b7789" }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={68}
              tick={{ fontSize: 11, fill: "#6b7789" }}
              tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))}
            />
            <Tooltip
              cursor={{ fill: "rgba(16,40,80,0.04)" }}
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <TooltipRico
                    titulo={String(label)}
                    itens={payload
                      .filter((x) => x.value !== null && x.value !== undefined)
                      .map((x) => ({
                        rotulo:
                          x.dataKey === "orcado"
                            ? "Orçado"
                            : x.dataKey === "realizado"
                              ? "Realizado"
                              : "Projetado",
                        cor: x.color,
                        valor: (x.value as number).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }),
                      }))}
                  />
                ) : null
              }
            />
            {projecao ? (
              <Line
                type="monotone"
                dataKey="orcado"
                stroke={COR.orcado}
                strokeWidth={1.5}
                dot={false}
              />
            ) : (
              <Bar dataKey="orcado" fill={COR.orcado} radius={[2, 2, 0, 0]} maxBarSize={28} />
            )}
            {projecao ? (
              <Line
                type="monotone"
                dataKey="realizado"
                stroke={COR.realizado}
                strokeWidth={2.5}
                dot={{ r: 2 }}
              />
            ) : (
              <Bar dataKey="realizado" fill={COR.realizado} radius={[2, 2, 0, 0]} maxBarSize={28} />
            )}
            {projecao && (
              <Line
                type="monotone"
                dataKey="projetado"
                stroke={COR.forecast}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={{ r: 2 }}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* ⚠️ FORA do `div` de altura fixa: dentro, o `ResponsiveContainer` ocupa
          os 100% e empurra a legenda para fora do cartão. */}
      <LegendaGrafico
        series={
          projecao
            ? [
                ["Orçado", COR.orcado],
                ["Realizado", COR.realizado],
                ["Projeção", COR.forecast],
              ]
            : [
                ["Orçado", COR.orcado],
                ["Realizado", COR.realizado],
              ]
        }
      />
    </>
  );
}
